import { create } from 'zustand';
import { useContainerStore } from '../../container';
import { genSyncTimezoneItem, genSyncTimezoneVolumeMountItem } from '../sync-timezone';

/** 卷挂载状态 */
export enum VolumeMountType {
  /** 不挂载 */
  NotMount = 'NotMount',
  /** 只读 */
  ReadOnly = 'ReadOnly',
  /** 读写 */
  ReadWrite = 'ReadWrite',
}

/** 每一个容器挂载卷的状态明细 - 注意跟 yaml 数据的区别,状态收集总是以简洁原始的 ui 数据为准, yaml 需要单独装换 */
export interface VolumeMountItem {
  /** 容器名称 */
  containerName: string;
  /** 挂载卷的类型 */
  mountType: VolumeMountType;
  /** 挂载路径 */
  mountPath?: string;
}

/** 挂载卷弹窗的 tab */
export enum VolumeMountTab {
  PERSISTENCE = 'Persistence',
  TEMPORARY = 'Temporary',
  HOST_PATH = 'HostPath',
}

/** 字典弹窗的 tab */
export enum VolumeKeymapTab {
  CONFIG_MAP = 'ConfigMap',
  SECRET_MAP = 'SecretMap',
}

/** 卷类型, 作用于 VolumeMountTab,VolumeMountTab 类似, 标识组件数据来源 */
export enum VolumeType {
  VOLUME_CLAIM_TEMPLATE = 'VolumeClaimTemplate',
}

/** 挂载关联 - 挂载卷|挂载配置字典或保密字典|持久卷声明模板|持久卷声明模板(statefulset) 都有 */
export interface VolumeMountsProps {
  /** 挂载关联 */
  volumeMounts?: VolumeMountItem[];
}

/** 挂载卷 */
export interface VolumeStorageItem extends VolumeMountsProps {
  /** 弹窗的 tab */
  type: VolumeMountTab;
  /** 选择的卷或者字典 */
  selected: string;
  /** 卷名称 */
  volumeName: string;
  /** 主机路径 */
  volumeHostPath: string;
}

/** 挂载配置字典或保密字典 */
export interface VolumeKeymapItem extends VolumeMountsProps {
  /** 弹窗的 tab */
  type: VolumeKeymapTab;
  /** 选择的卷或者字典 */
  selected: string;
  /** 卷名称 */
  volumeName: string;
  /** 特定的键 */
  items: { key: string; path: string }[];
}

/** 持久卷声明模板 */
export interface VolumeClaimTemplateItem extends VolumeMountsProps {
  /** 类型 - 扩展的时候, 这个属性是为了后续方便判断类型用的 */
  type: VolumeType.VOLUME_CLAIM_TEMPLATE;
  /** PVC前缀 - 起名的时候也是为了方便扩展, 因为方法中用到了名称判断 */
  volumeName?: string;
  /** 存储类 */
  storageClass?: string;
  /** 访问模式 */
  accessModes?: string[];
  /** 卷容量 */
  volumeCapacity?: number;
}

/** 存储设置的展示的项 */
export type VolumeItem = VolumeStorageItem | VolumeKeymapItem | VolumeClaimTemplateItem;

/**
 * 判断是否为 VolumeStorageItem 类型
 */
export function isVolumeStorageItem(item: VolumeItem): item is VolumeStorageItem {
  return Object.values(VolumeMountTab).includes(item?.type as VolumeMountTab);
}

/**
 * 判断是否为 VolumeKeymapItem 类型
 */
export function isVolumeKeymapItem(item: VolumeItem): item is VolumeKeymapItem {
  return Object.values(VolumeKeymapTab).includes(item?.type as VolumeKeymapTab);
}

/** 判断是否为 VolumeClaimTemplateItem 类型
 */
export function isVolumeClaimTemplateItem(item: VolumeItem): item is VolumeClaimTemplateItem {
  return item?.type === VolumeType.VOLUME_CLAIM_TEMPLATE;
}

/** 存储设置状态 */
interface StorageSettingState {
  volumes: VolumeItem[];
}

/** 初始状态 */
const initialState: StorageSettingState = {
  volumes: [],
};

interface StorageSettingAction {
  /** 存储设置添加卷 - 需要操作 containters 关联上对应的卷 */
  addVolume: (volume: VolumeItem) => void;
  /** 编辑指定的卷(volumeName是可以修改的)  - 需要操作 containters 关联上对应的卷 */
  updateVolume: (volume: VolumeItem, index: number) => void;
  /** 删除指定的卷 */
  removeVolume: (volume: VolumeItem) => void;
  /** 是否同步主机时区的容器 */
  addSyncTimeZoneVolume: (containerName: string) => void;
  /** 删除同步主机时区的容器 */
  removeSyncTimeZoneVolume: (containerName: string) => void;
  /** 设置整个数组 */
  setVolumes: (volumes: VolumeItem[]) => void;
  /** 清洗数据,当容器列表变化的时候,清除不存在的容器关联 */
  cleanVolumes: () => void;
  /** 重置 */
  reset: () => void;
}

export const useStorageSettingStore = create<StorageSettingState & StorageSettingAction>()(
  (set) => ({
    ...initialState,
    addVolume: (volume) => {
      /** 处理容器挂载卷 */
      useContainerStore.getState().addContainerVolumeMount(volume);
      set((state) => ({ volumes: [...state.volumes, volume] }));
    },
    updateVolume: (volume, index) => {
      /** 处理容器挂载卷 */
      set((state) => {
        const oldVolume = state.volumes[index];
        if (!oldVolume) {
          return state; // 如果索引不存在，直接返回当前状态
        }
        useContainerStore.getState().updateContainerVolumeMount(volume, oldVolume);

        const updatedVolumes = state.volumes.map((v, idx) => (idx === index ? volume : v));
        return { volumes: updatedVolumes };
      });
    },
    addSyncTimeZoneVolume: (containerName) => {
      const newVolume = genSyncTimezoneItem(containerName);
      const newVolumeMount = genSyncTimezoneVolumeMountItem(containerName);
      set((state) => {
        // 查找是否已存在同名的时区同步卷
        const existIndex = state.volumes.findIndex((v) => v.volumeName === newVolume.volumeName);
        if (existIndex !== -1) {
          // 如果卷已存在，检查是否已有该容器的挂载
          const existVolume = state.volumes[existIndex];
          const hasContainerMount = existVolume.volumeMounts?.some(
            (vm) => vm.containerName === containerName,
          );

          if (!hasContainerMount) {
            // 如果没有该容器的挂载，添加新的挂载
            const updatedVolumes = state.volumes.map((volume, index) => {
              if (index === existIndex) {
                return {
                  ...volume,
                  volumeMounts: [...(volume.volumeMounts || []), newVolumeMount],
                };
              }
              return volume;
            });

            return { volumes: updatedVolumes };
          }

          return state;
        }
        return { volumes: [...state.volumes, newVolume] };
      });
    },
    removeSyncTimeZoneVolume: (containerName) => {
      const volume = genSyncTimezoneItem(containerName);
      const syncTimeZoneVolumeName = volume.volumeName;
      set((state) => ({
        volumes: state.volumes.filter((v) => {
          return !(
            isVolumeStorageItem(v) &&
            v.volumeName === syncTimeZoneVolumeName &&
            v.volumeMounts?.some((vm) => vm.containerName === containerName)
          );
        }),
      }));
    },
    removeVolume: (volume) => {
      /** 清理容器的挂载卷关联 */
      useContainerStore.getState().removeContainerVolumeMount(volume);
      set((state) => ({
        volumes: state.volumes.filter((v) => v.volumeName !== volume.volumeName),
      }));
    },
    setVolumes: (volumes) => set({ volumes }),
    cleanVolumes: () => {
      const containerNames = useContainerStore
        .getState()
        .containersArray.map((c) => c.containerSetting.containerName);

      set((state) => ({
        volumes: state.volumes.map((v) => {
          return {
            ...v,
            volumeMounts: v.volumeMounts?.filter((vm) => containerNames.includes(vm.containerName)),
          };
        }),
      }));
    },
    reset: () => set(initialState),
  }),
);
